import { useRef } from 'react';
import * as React from 'react';
import { useRouter } from 'next/router';

import {
    estimateExplainerButtonStyle,
    styledListStyle,
    styledListItemStyle,
    styledConfidenceStyle,
    textStyle,
    confidenceHeadingStyle,
    listHeadingStyle,
    styledEstimateExplainerStyle,
    styledStackStyle,
} from './EstimateExplainer.styles.css';
import { Badge } from '@zoopla-eng/zds-components/badges';
import { Link } from '@zoopla-eng/zds-components/link';
import {
    TickMedium,
    CloseMedium,
    ChevronRightSmall,
} from '@zoopla-eng/zds-components/icons';
import { Box } from '@zoopla-eng/zds-components/box';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { getUPRNfromQuery } from '@zoopla/property-from-query';
import { triggerTracking } from '@zoopla/tracking';
import { ButtonRight } from '@zoopla-eng/zds-components/button';

import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

export const buttonText = 'How we calculate and rate our estimates';
export const modalTitle = 'How we calculate and rate our estimates';

export const EstimateExplainer: React.FC<
    React.PropsWithChildren<unknown>
> = () => {
    const triggerOrigin = useRef(null);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const router = useRouter();
    const uprn = getUPRNfromQuery(router.query);

    const tracking = {
        event: 'GAEvent',
        eventCategory: 'property_details',
        eventValue: uprn ? parseInt(uprn, 10) : 0,
    };

    const openAndTrackModal = (): void => {
        triggerTracking({
            ...tracking,
            eventCategory: 'property details',
            eventAction: 'click',
            eventLabel: 'estimate info',
        });
        setIsModalOpen(true);
    };

    const closeAndTrackModal = (): void => {
        triggerTracking({
            ...tracking,
            eventAction: 'close',
            eventLabel: 'estimate_info',
        });
        setIsModalOpen(false);
    };

    return (
        <div className={styledEstimateExplainerStyle}>
            <div className={estimateExplainerButtonStyle}>
                <ButtonRight
                    category="tertiary"
                    onClick={openAndTrackModal}
                    data-testid="modal-launch-btn"
                    ref={triggerOrigin}
                    Icon={ChevronRightSmall}
                >
                    {buttonText}
                </ButtonRight>
            </div>
            <EstimateExplainerModal
                isOpen={isModalOpen}
                close={closeAndTrackModal}
                moreInfoTracking={tracking}
            />
        </div>
    );
};

const Tick = () => (
    <div>
        <TickMedium color="functionalSuccess" />
    </div>
);

const Cross = () => (
    <div>
        <CloseMedium color="functionalFailure" />
    </div>
);

export function EstimateExplainerModal({
    isOpen,
    close,
    moreInfoTracking,
}: {
    isOpen: boolean;
    close: () => void;
    moreInfoTracking: {
        event: string;
        eventCategory: string;
        eventValue: number;
    };
}) {
    return (
        <ControlledDialog
            variant="medium"
            mobileVariant="medium"
            label={modalTitle}
            title={modalTitle}
            isOpen={isOpen}
            onOpenChange={close}
        >
            <div className={styledStackStyle}>
                <p className={`${textStyle} ${fontGroups.whisk}`}>
                    We update our estimates every month based on the info we get
                    from regulated data sources. Our computer algorithm then
                    generates an estimate based on your home and similar
                    properties nearby. Magic.
                </p>

                <Box>
                    <h2 className={`${listHeadingStyle} ${fontGroups.whisk}`}>
                        We include:
                    </h2>
                    <ul className={styledListStyle}>
                        <li className={styledListItemStyle}>
                            <Tick />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Official survey records from accredited
                                surveyors
                            </p>
                        </li>
                        <li className={styledListItemStyle}>
                            <Tick />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Sold house price data from HM Land Registry and
                                Registers of Scotland
                            </p>
                        </li>
                        <li className={styledListItemStyle}>
                            <Tick />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Energy performance certificates (EPCs)
                            </p>
                        </li>
                        <li className={styledListItemStyle}>
                            <Tick />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Live for sale and to rent property listings
                            </p>
                        </li>
                        <li className={styledListItemStyle}>
                            <Tick />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Address data from Royal Mail and Ordnance Survey
                            </p>
                        </li>
                    </ul>
                </Box>
                <Box>
                    <h2 className={`${listHeadingStyle} ${fontGroups.whisk}`}>
                        We do not include:
                    </h2>
                    <ul className={styledListStyle}>
                        <li className={styledListItemStyle}>
                            <Cross />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Renovations and makeovers
                            </p>
                        </li>
                        <li className={styledListItemStyle}>
                            <Cross />
                            <p className={`${textStyle} ${fontGroups.whisk}`}>
                                Recent extensions and extra rooms
                            </p>
                        </li>
                    </ul>
                </Box>
                <p className={`${textStyle} ${fontGroups.whisk}`}>
                    For more information on our estimates,{' '}
                    <Link
                        key="home-values-link"
                        data-testid="home-values-link"
                        target="_blank"
                        href="https://help.zoopla.co.uk/hc/en-gb/sections/360001665778-Zoopla-estimates"
                        onClick={(): void => {
                            triggerTracking({
                                ...moreInfoTracking,
                                eventAction: 'clickthrough',
                                eventLabel: 'estimate_explainer>help',
                            });
                        }}
                    >
                        see our FAQs
                    </Link>
                    .
                </p>

                <Box>
                    <h2
                        className={`${confidenceHeadingStyle} ${fontGroups.mug}`}
                    >
                        Our confidence ratings
                    </h2>
                    <Badge level={2} text="HIGH CONFIDENCE" />
                    <div className={styledConfidenceStyle}>
                        <p className={`${textStyle} ${fontGroups.whisk}`}>
                            There are a lot of homes like this in the area. The
                            data is up to date and market conditions are steady.
                            This is a common property in the area.
                        </p>
                    </div>
                    <Badge level={2} text="LOW CONFIDENCE" />
                    <div className={styledConfidenceStyle}>
                        <p className={`${textStyle} ${fontGroups.whisk}`}>
                            There are very few similar properties nearby. The
                            data is less recent and market conditions are highly
                            changeable. The property might be very unique.
                        </p>
                    </div>
                </Box>
            </div>
        </ControlledDialog>
    );
}
